import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useCurrentCharacterName } from "../hooks/useCurrentCharacterName";
import {
  buildGroupTalkPreviewMembers,
  buildParticipantsFromTemplate,
  createParticipant,
  findGroupTalkMember,
  groupTalkMemberOptions,
  groupTalkStanceOptions,
  groupTalkTemplates,
  isDuplicateParticipant,
  type GroupTalkMemberId,
  type GroupTalkParticipant,
  type GroupTalkStanceId,
  type GroupTalkTemplateId,
} from "../lib/groupTalk";
import {
  loadGroupTalkDraft,
  saveGroupTalkDraft,
  type GroupTalkDraft,
} from "../lib/groupTalkSessionStorage";

type GroupTalkHubRouteState = {
  errorMessage?: string;
};

const templateOrder: GroupTalkTemplateId[] = ["custom", "management", "project", "professional"];

function getTemplateBadgeLabel(templateId: GroupTalkTemplateId) {
  return templateId === "custom" ? null : "テンプレート";
}

export default function GroupTalkHub() {
  const navigate = useNavigate();
  const location = useLocation();
  const characterName = useCurrentCharacterName();
  const routeState = (typeof location.state === "object" && location.state ? location.state : {}) as GroupTalkHubRouteState;
  const initialDraft = useMemo(() => loadGroupTalkDraft(), []);
  const initialTemplateId = initialDraft?.templateId ?? "management";

  const [selectedTemplateId, setSelectedTemplateId] = useState<GroupTalkTemplateId>(initialTemplateId);
  const [participants, setParticipants] = useState<GroupTalkParticipant[]>(
    () => initialDraft?.participants ?? buildParticipantsFromTemplate(initialTemplateId)
  );
  const [addRoleId, setAddRoleId] = useState<GroupTalkMemberId>("cmo");
  const [addStanceId, setAddStanceId] = useState<GroupTalkStanceId>("bold");
  const [addError, setAddError] = useState("");
  const [routeError, setRouteError] = useState(routeState.errorMessage ?? "");

  const templateMap = useMemo(
    () => new Map(groupTalkTemplates.map((template) => [template.id, template])),
    []
  );
  const orderedTemplates = useMemo(
    () => templateOrder.map((id) => templateMap.get(id)).filter((template): template is NonNullable<typeof template> => Boolean(template)),
    [templateMap]
  );
  const selectedTemplate = templateMap.get(selectedTemplateId) ?? groupTalkTemplates[0];
  const previewMembers = useMemo(
    () => buildGroupTalkPreviewMembers(participants, characterName),
    [characterName, participants]
  );

  useEffect(() => {
    const draft: GroupTalkDraft = {
      templateId: selectedTemplateId,
      title: selectedTemplate.title,
      description: selectedTemplate.description,
      participants,
    };
    saveGroupTalkDraft(draft);
  }, [participants, selectedTemplate.description, selectedTemplate.title, selectedTemplateId]);

  const selectTemplate = (templateId: GroupTalkTemplateId) => {
    setSelectedTemplateId(templateId);
    setParticipants(buildParticipantsFromTemplate(templateId));
    setAddError("");
    setRouteError("");
  };

  const removeParticipant = (participantId: string) => {
    setParticipants((current) => current.filter((participant) => participant.participantId !== participantId));
    setAddError("");
  };

  const updateParticipantStance = (participantId: string, stanceId: GroupTalkStanceId) => {
    setParticipants((current) => {
      const target = current.find((participant) => participant.participantId === participantId);
      if (!target || target.memberId === "mei") return current;
      if (isDuplicateParticipant(current, target.memberId, stanceId, participantId)) {
        setAddError("同じ役割とスタンスの組み合わせは追加できません。");
        return current;
      }
      setAddError("");
      return current.map((participant) =>
        participant.participantId === participantId ? { ...participant, stanceId } : participant
      );
    });
  };

  const addParticipant = () => {
    if (isDuplicateParticipant(participants, addRoleId, addStanceId)) {
      setAddError("同じ役割とスタンスの組み合わせは追加できません。");
      return;
    }
    setParticipants((current) => [...current, createParticipant(addRoleId, addStanceId)]);
    setAddError("");
  };

  const openPreview = () => {
    const nonFacilitatorCount = participants.filter((participant) => participant.memberId !== "mei").length;
    if (selectedTemplateId === "custom" && nonFacilitatorCount === 0) {
      setRouteError("自由に組む場合は、メイ以外のメンバーを1人以上追加してください。");
      return;
    }

    setRouteError("");
    navigate("/talk/group/preview", {
      state: {
        draft: {
          templateId: selectedTemplateId,
          title: selectedTemplate.title,
          description: selectedTemplate.description,
          participants,
        },
        members: previewMembers,
      },
    });
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-3xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/talk" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ← 戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">みんなで話す</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2.4rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#A67C52]">Group Talk</p>
            <h1 className="mt-3 text-3xl font-black tracking-tight text-[#7C5A4E]">会議のたたき台を選ぶ</h1>
          </div>

          {routeError ? <p className="mt-4 text-sm font-bold text-[#C06D5A]">{routeError}</p> : null}

          <div className="mt-6 space-y-3">
            <Link
              to="/talk/group/sessions"
              className="flex items-center justify-between rounded-[1.6rem] border border-[#D9C5B9] bg-[#FFF6EF] px-5 py-4 text-left shadow-sm transition hover:bg-[#FFF1E8]"
            >
              <span>
                <span className="block text-base font-black text-[#5D4D46]">続きから始める</span>
                <span className="mt-1 block text-sm text-[#7B6861]">以前の会議セッションを開きます。</span>
              </span>
              <span className="text-sm font-black text-[#A67C52]">進む →</span>
            </Link>

            {orderedTemplates.map((template) => {
              const selected = template.id === selectedTemplateId;
              const badge = getTemplateBadgeLabel(template.id);

              return (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => selectTemplate(template.id)}
                  className={`flex w-full items-center justify-between rounded-[1.5rem] border px-5 py-4 text-left transition ${
                    selected
                      ? "border-[#D1907B] bg-[#FFF7F1] shadow-sm"
                      : "border-[#EADBC8] bg-white hover:bg-[#FEFBF9]"
                  }`}
                >
                  <span className="text-base font-black text-[#5D4D46]">{template.title}</span>
                  {badge ? (
                    <span className="inline-flex shrink-0 rounded-full bg-white px-3 py-1 text-[11px] font-black text-[#A46D4C]">
                      {badge}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="mt-8 rounded-[2rem] border border-[#EADBC8] bg-[#FFFBF8] p-5">
            <h2 className="text-xl font-black text-[#7C5A4E]">{selectedTemplate.title}</h2>
            <p className="mt-2 text-sm leading-6 text-[#6B5851]">{selectedTemplate.description}</p>

            <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Link
                to="/talk"
                className="rounded-full border border-[#EADBC8] bg-white px-5 py-3 text-center text-sm font-bold text-[#5D4D46]"
              >
                戻る
              </Link>
              <button
                type="button"
                onClick={openPreview}
                className="rounded-full bg-[#5D4D46] px-6 py-3 text-sm font-black text-white transition hover:bg-[#4A3E39]"
              >
                このメンバーで進める
              </button>
            </div>

            <div className="mt-6">
              <p className="text-sm font-black text-[#5D4D46]">参加中メンバー</p>
              <div className="mt-3 space-y-4">
                {participants.map((participant) => {
                  const member = findGroupTalkMember(participant.memberId);
                  const stanceLabel =
                    groupTalkStanceOptions.find((option) => option.id === participant.stanceId)?.label ?? null;
                  const displayName =
                    participant.memberId === "mei"
                      ? characterName
                      : stanceLabel
                        ? `${member.label}（${stanceLabel}）`
                        : member.label;

                  return (
                    <div key={participant.participantId} className="rounded-[1.6rem] border border-[#EADBC8] bg-white p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-base font-black text-[#5D4D46]">{displayName}</h3>
                          <p className="mt-1 text-sm text-[#6B5851]">役割: {member.roleLabel}</p>
                        </div>
                        {participant.fixed ? (
                          <span className="rounded-full bg-[#F5EEE7] px-3 py-1 text-[11px] font-black text-[#8F7468]">
                            固定参加
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => removeParticipant(participant.participantId)}
                            className="rounded-full border border-[#EADBC8] px-3 py-1 text-[11px] font-black text-[#8F7468]"
                          >
                            削除
                          </button>
                        )}
                      </div>

                      {participant.memberId === "mei" ? (
                        <p className="mt-4 text-sm font-bold text-[#8F7468]">
                          メイはファシリテータとして参加し、スタンス設定はありません。
                        </p>
                      ) : (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {groupTalkStanceOptions.map((stance) => {
                            const selected = participant.stanceId === stance.id;
                            return (
                              <button
                                key={stance.id}
                                type="button"
                                onClick={() => updateParticipantStance(participant.participantId, stance.id)}
                                className={`rounded-full border px-3 py-2 text-sm font-bold transition ${
                                  selected
                                    ? "border-[#D1907B] bg-[#FFF3EC] text-[#7C5A4E]"
                                    : "border-[#EADBC8] bg-white text-[#6B5851] hover:bg-[#FEFBF9]"
                                }`}
                              >
                                {stance.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 rounded-[1.6rem] border border-dashed border-[#D9C5B9] bg-white p-4">
              <p className="text-sm font-black text-[#5D4D46]">メンバーを追加する</p>
              <p className="mt-2 text-sm leading-6 text-[#6B5851]">
                参加メンバーは増減可能。同じ役割も別スタンスで追加できます。
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
                <label className="text-sm font-bold text-[#6B5851]">
                  役割
                  <select
                    value={addRoleId}
                    onChange={(event) => setAddRoleId(event.target.value as GroupTalkMemberId)}
                    className="mt-2 w-full rounded-[1rem] border border-[#EADBC8] bg-[#FFFDFB] px-3 py-3 text-sm"
                  >
                    {groupTalkMemberOptions
                      .filter((option) => option.id !== "mei")
                      .map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="text-sm font-bold text-[#6B5851]">
                  スタンス
                  <select
                    value={addStanceId}
                    onChange={(event) => setAddStanceId(event.target.value as GroupTalkStanceId)}
                    className="mt-2 w-full rounded-[1rem] border border-[#EADBC8] bg-[#FFFDFB] px-3 py-3 text-sm"
                  >
                    {groupTalkStanceOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={addParticipant}
                  className="self-end rounded-[1rem] border border-[#D9C5B9] bg-white px-5 py-3 text-sm font-black text-[#6B5851] transition hover:bg-[#FEFBF9]"
                >
                  追加
                </button>
              </div>
              {addError ? <p className="mt-3 text-sm font-bold text-[#C06D5A]">{addError}</p> : null}
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Link
                to="/talk"
                className="rounded-full border border-[#EADBC8] bg-white px-5 py-3 text-center text-sm font-bold text-[#5D4D46]"
              >
                戻る
              </Link>
              <button
                type="button"
                onClick={openPreview}
                className="rounded-full bg-[#5D4D46] px-6 py-3 text-sm font-black text-white transition hover:bg-[#4A3E39]"
              >
                このメンバーで進める
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
